import {
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from 'react'
import type { ReactElement, Ref } from 'react'
import { createPortal } from 'react-dom'
import { createPopper } from '@popperjs/core'
import type { Instance, Modifier, Options, Placement } from '@popperjs/core'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnchorEl = Element | (() => Element) | null | undefined | any

interface Props {
  /**
   * Applied to the portaled element.
   */
  id?: string
  /**
   * This is the DOM element, or a function that returns the DOM element,
   * that may be used to set the position of the popover.
   * The return value will passed as the reference object of the Popper
   * instance.
   */
  anchorEl: AnchorEl
  /**
   * The DOM element the popper is portaled into. Defaults to `document.body`.
   */
  container?: Element | null
  /**
   * The popper content. Must be a single element so the positioning ref can be
   * attached to it without introducing an extra wrapper node.
   */
  children: ReactElement
  /**
   * Always keep the children in the DOM.
   * This property can be useful in SEO situation or
   * when you want to maximize the responsiveness of the Popper.
   */
  keepMounted?: boolean
  /**
   * Popper.js modifiers. See https://popper.js.org/docs/v2/modifiers/
   */
  modifiers?: Array<Partial<Modifier<string, object>>>
  /**
   * If `true`, the popper is visible.
   */
  open: boolean
  /**
   * Popper placement.
   */
  placement?: Placement
  /**
   * Options provided to the [`@popperjs/core`](https://popper.js.org/) instance.
   */
  options?: Partial<Options>
}

function getAnchorEl(anchorEl: AnchorEl): Element | null {
  return typeof anchorEl === 'function' ? anchorEl() : anchorEl
}

/**
 * Poppers rely on the 3rd party library
 * [Popper.js](https://popper.js.org/) for positioning.
 */
const Popper = ({
  id,
  anchorEl,
  container = null,
  children,
  keepMounted = false,
  modifiers,
  open,
  placement = 'bottom',
  options,
}: Props) => {
  const popperNode = useRef<HTMLElement | null>(null)
  const instance = useRef<Instance | null>(null)
  // ポータル先の DOM はクライアントにしか存在しないため、マウント後に描画する
  const [ isMounted, setMounted ] = useState(false)

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    const reference = getAnchorEl(anchorEl)
    if (!open || !reference || !popperNode.current) {
      return undefined
    }

    const { modifiers: optionModifiers, ...restOptions } = options || {}
    instance.current = createPopper(reference, popperNode.current, {
      placement,
      ...restOptions,
      modifiers: [
        { name: 'preventOverflow', options: { rootBoundary: 'viewport' }},
        ...(modifiers || []),
        ...(optionModifiers || []),
      ],
    })

    return () => {
      instance.current?.destroy()
      instance.current = null
    }
  }, [ anchorEl, modifiers, open, options, placement ])

  if (!isMounted || (!keepMounted && !open)) {
    return null
  }

  const content = isValidElement(children)
    ? cloneElement(children, { id, ref: popperNode } as { id?: string, ref: Ref<HTMLElement> })
    : children

  return createPortal(content, container || document.body)
}

export default Popper
